import { doc, runTransaction } from "firebase/firestore";
import { auth, db } from "../Backend/Firebase";

// All MyPreset persistence lives here. There are no Firestore listeners:
// Panel opens use a 30-minute freshness window. Local saves and manual retries
// bypass it; there is no periodic background timer for fetching fresh data.
export const PRESET_FRESHNESS_MS = 30 * 60 * 1000;
const services = new Map();
const actor = makeId();
let sequence = 0;

function makeId() {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Old presets have no IDs. Deterministic IDs let two devices migrate the same
// old backup without duplicating it. New presets always receive random IDs.
export function normalizePresets(presets = []) {
  if (!Array.isArray(presets)) throw new Error("Invalid preset list");
  const occurrences = new Map();
  return presets.map((preset) => {
    if (!preset || typeof preset.presetName !== "string" || !Array.isArray(preset.presetTasks)) {
      throw new Error("Invalid preset data");
    }
    const count = occurrences.get(preset.presetName) || 0;
    occurrences.set(preset.presetName, count + 1);
    return { ...preset, id: preset.id || `legacy-${encodeURIComponent(preset.presetName)}-${count}` };
  });
}

function canonical(value) {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
  }
  return value;
}

function samePreset(left, right) {
  return JSON.stringify(canonical(left || null)) === JSON.stringify(canonical(right || null));
}

// Apply only queued item operations to the latest backend list. A transaction
// retries this merge if another device writes concurrently. Receipts acknowledge
// each page's ordered operations, making retries safe after a lost response.
// Conflicting edits/deletes preserve data as an explicitly named conflict copy.
export function mergePresetOperations(data = {}, operations = []) {
  const presets = normalizePresets(data.userPandaPresets);
  const receipts = { ...data.presetSyncReceipts };
  for (const operation of operations) {
    if ((receipts[operation.actor] || 0) >= operation.sequence) continue;
    if (operation.type === "reorder") {
      // Reorder only known IDs in their existing slots. Concurrent additions,
      // deletions and edits remain intact, including hidden filtered presets.
      const byId = new Map(presets.map((preset) => [preset.id, preset]));
      const ordered = [...new Set(operation.ids)].filter((id) => byId.has(id));
      const selected = new Set(ordered);
      let next = 0;
      presets.forEach((preset, index) => {
        if (selected.has(preset.id)) presets[index] = byId.get(ordered[next++]);
      });
      receipts[operation.actor] = operation.sequence;
      continue;
    }
    const index = presets.findIndex((preset) => preset.id === operation.id);
    const remote = presets[index] || null;
    const desired = operation.preset;
    const conflict = !samePreset(remote, operation.base) && !samePreset(remote, desired);
    if (conflict) {
      const preserved = desired || remote;
      if (preserved) {
        presets.push({
          ...preserved,
          id: `conflict-${operation.token}`,
          presetName: `${preserved.presetName} (conflict copy)`,
        });
      }
      // For deletes, retain the newer remote edit under a new ID while still
      // honoring the deletion of the original. Edits leave the remote intact.
      if (!desired && index !== -1) presets.splice(index, 1);
    } else if (desired) {
      if (index === -1) presets.push(desired);
      else presets[index] = desired;
    } else if (index !== -1) {
      presets.splice(index, 1);
    }
    receipts[operation.actor] = operation.sequence;
  }
  return { userPandaPresets: presets, presetSyncReceipts: receipts, presetSyncRevision: data.presetSyncRevision || 0 };
}

async function syncWithFirestore(uid, operations) {
  const ref = doc(db, "users", uid);
  // Keep the existing user-document path and access rules. The whole list is
  // stored together, but edits merge by ID instead of replacing another device's
  // list. Profile fields remain untouched through merge:true.
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    const data = snapshot.exists() ? snapshot.data() : {};
    const result = mergePresetOperations(data, operations);
    const hasUnapplied = operations.some((operation) =>
      (data.presetSyncReceipts?.[operation.actor] || 0) < operation.sequence);
    if (hasUnapplied || (data.userPandaPresets || []).some((preset) => !preset.id)) {
      result.presetSyncRevision += 1;
      transaction.set(ref, { ...result, presetSyncVersion: 1, lastUpdatedDate: new Date().toISOString() }, { merge: true });
    }
    return result;
  });
}

export function createPresetSync(uid, {
  storage = localStorage,
  backend = syncWithFirestore,
  canSync = () => auth.currentUser?.uid === uid,
} = {}) {
  const cacheKey = `myPresetSync:${uid}:cache`;
  const pendingPrefix = `myPresetSync:${uid}:pending:`;
  const subscribers = new Set();
  let state = { presets: [], status: "saving", error: "", lastSyncedAt: null };
  let running = null;
  let requested = false;
  let retryTimer;
  let started = false;
  let disposed = false;

  function emit(patch) {
    state = { ...state, ...patch };
    subscribers.forEach((callback) => callback(state));
  }

  function readCache() {
    return JSON.parse(storage.getItem(cacheKey) || '{"userPandaPresets":[]}');
  }

  function readPending() {
    // Separate outbox keys avoid two tabs replacing each other's pending edits.
    return Object.keys(storage).filter((key) => key.startsWith(pendingPrefix))
      .map((key) => JSON.parse(storage.getItem(key)))
      .sort((a, b) => a.createdAt - b.createdAt || a.sequence - b.sequence || a.token.localeCompare(b.token));
  }

  function refreshLocal() {
    const cached = readCache();
    const result = mergePresetOperations(cached, readPending());
    emit({ presets: result.userPandaPresets, lastSyncedAt: cached.lastSyncedAt || null });
  }

  function fail(error, local = false) {
    emit({
      status: "error",
      error: local
        ? "Local storage could not be read or saved. Download a backup before continuing."
        : "Could not sync with the backend. Your saved local presets remain available. Click the dot to retry.",
    });
    if (!local && !disposed && canSync()) {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(sync, 30000);
    }
  }

  function queue(preset, base) {
    // Preserve causal order even if the device clock is adjusted backwards.
    const createdAt = readPending().reduce((time, item) => Math.max(time, item.createdAt + 1), Date.now());
    const operation = {
      actor, sequence: ++sequence, token: makeId(),
      id: (preset || base).id, preset, base: base || null, createdAt,
    };
    // Persist BEFORE reporting success or attempting the backend. A reload or
    // closed panel cannot discard an unsent edit/deletion.
    storage.setItem(`${pendingPrefix}${operation.token}`, JSON.stringify(operation));
  }

  function reorder(ids) {
    try {
      initialize();
      const createdAt = readPending().reduce((time, item) => Math.max(time, item.createdAt + 1), Date.now());
      const operation = { type: "reorder", ids, actor, sequence: ++sequence, token: makeId(), createdAt };
      storage.setItem(`${pendingPrefix}${operation.token}`, JSON.stringify(operation));
      refreshLocal();
      emit({ status: "saving", error: "" });
      sync();
      return true;
    } catch (error) {
      fail(error, true);
      return false;
    }
  }

  function initialize() {
    // The old global key has no owner information. Claim it for the first
    // signed-in account once, leave the original untouched as a recovery copy,
    // and never expose that account's data to later accounts on this browser.
    if (!storage.getItem(cacheKey)) {
      const ownerKey = "myPresetSync:legacyOwner";
      const owner = storage.getItem(ownerKey);
      if (!owner || owner === uid) {
        const legacy = normalizePresets(JSON.parse(storage.getItem("userPandaPresets") || "[]"));
        storage.setItem(ownerKey, uid);
        // Resume interrupted migration rather than queueing the same item twice.
        const existing = new Set(readPending().map((operation) => operation.id));
        legacy.forEach((preset) => { if (!existing.has(preset.id)) queue(preset, null); });
      }
      storage.setItem(cacheKey, JSON.stringify({ userPandaPresets: [] }));
    }
    refreshLocal();
  }

  async function performSync() {
    clearTimeout(retryTimer);
    if (disposed) return;
    if (!canSync()) {
      emit({ status: "error", error: "Sign in again to sync. Your local changes are kept on this device." });
      return;
    }
    if (navigator.onLine === false) {
      fail(new Error("Offline"));
      return;
    }
    emit({ status: "saving", error: "" });
    let timeout;
    let pending;
    try {
      // Check local data before fetching, so damaged storage is not overwritten.
      initialize();
      pending = readPending();
    } catch (error) {
      clearTimeout(timeout);
      fail(error, true);
      return;
    }
    try {
      // A stalled request must not leave the dot yellow or block manual retry
      // forever. A late server commit is safe: its receipts prevent replay when
      // the next request retries the still-persisted outbox.
      const result = await Promise.race([
        backend(uid, pending),
        new Promise((resolve, reject) => {
          timeout = setTimeout(() => reject(new Error("Sync timed out")), 12000);
        }),
      ]);
      if (disposed) return;
      if (!canSync()) {
        emit({ status: "error", error: "Sign in again to sync. Your local changes are kept on this device." });
        return;
      }
      try {
        // Save the acknowledged backend snapshot before removing outbox items.
        // Receipts make a retry safe if a storage error interrupts this step.
        const cached = readCache();
        // Another tab may have already cached a newer transaction while this
        // response was travelling. Never replace that snapshot with an older one.
        const latest = (cached.presetSyncRevision || 0) > (result.presetSyncRevision || 0) ? cached : result;
        storage.setItem(cacheKey, JSON.stringify({ ...latest, lastSyncedAt: cached.lastSyncedAt || null }));
        pending.forEach((operation) => storage.removeItem(`${pendingPrefix}${operation.token}`));
        refreshLocal();
        if (readPending().length) {
          requested = true; // Edits made while this request was running.
        } else if (navigator.onLine === false) {
          fail(new Error("Offline"));
        } else {
          clearTimeout(retryTimer);
          // Persist only after the full outbox is acknowledged. Failed saves
          // never extend freshness, and this account's timestamp survives reloads.
          const lastSyncedAt = new Date().toISOString();
          storage.setItem(cacheKey, JSON.stringify({ ...readCache(), lastSyncedAt }));
          emit({ status: "synced", error: "", lastSyncedAt });
        }
      } catch (error) {
        fail(error, true);
      }
    } catch (error) {
      fail(error);
    } finally {
      clearTimeout(timeout);
    }
  }

  function sync() {
    if (running) {
      requested = true;
      return running;
    }
    running = (async () => {
      do {
        requested = false;
        await performSync();
      } while (requested && canSync() && !disposed);
    })().finally(() => { running = null; });
    return running;
  }

  function save(preset, base = null) {
    try {
      initialize();
      queue(preset ? { ...preset, id: preset.id || makeId() } : null, base);
      refreshLocal();
      emit({ status: "saving", error: "" });
      sync();
      return true;
    } catch (error) {
      fail(error, true);
      return false;
    }
  }

  function refreshIfNeeded() {
    if (running) return running;
    try {
      initialize();
      const age = Date.now() - Date.parse(state.lastSyncedAt);
      // Pending edits, errors, missing/invalid dates, and clock rollback all
      // require a sync. Simply leaving the panel open never expires a timer.
      if (!readPending().length && state.status !== "error" &&
          Number.isFinite(age) && age >= 0 && age < PRESET_FRESHNESS_MS &&
          navigator.onLine !== false && canSync()) {
        emit({ status: "synced", error: "" });
        return Promise.resolve();
      }
      return sync();
    } catch (error) {
      fail(error, true);
      return Promise.resolve();
    }
  }

  const onOnline = () => { if (canSync()) refreshIfNeeded(); };
  const onOffline = () => { if (canSync()) fail(new Error("Offline")); };
  const onStorage = (event) => {
    if (event.key === cacheKey || event.key?.startsWith(pendingPrefix)) {
      try {
        refreshLocal();
        // Green is only set by our own confirmed backend request.
        if (canSync()) refreshIfNeeded();
      } catch (error) { fail(error, true); }
    }
  };

  try { initialize(); } catch (error) { fail(error, true); }

  return {
    getSnapshot: () => state,
    subscribe(callback) { subscribers.add(callback); return () => subscribers.delete(callback); },
    save,
    reorder,
    sync,
    start() {
      if (!started) {
        started = true;
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        window.addEventListener("storage", onStorage);
      }
      return refreshIfNeeded();
    },
    dispose() {
      disposed = true;
      clearTimeout(retryTimer);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("storage", onStorage);
      subscribers.clear();
    },
  };
}

// Reuse the service across panel mounts, including when a save is in flight.
// Requests and background retries always verify the current authenticated UID.
export function getPresetSync(uid) {
  if (!services.has(uid)) services.set(uid, createPresetSync(uid));
  return services.get(uid);
}
